<template>
    <header class="place-header">
        <div class="place-header-bar">
            <button type="button" class="place-header-back" v-if="showBack" @click="goBack">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#1f2a37" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <line x1="19" y1="12" x2="5" y2="12"></line>
                    <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
            </button>

            <img v-if="logoUrl" :src="logoUrl" :alt="placeName" class="place-header-logo" />

            <div class="place-header-menu">
                <benefits-menu :is-logged="isLogged" @select="onMenuSelect" />
            </div>

            <div class="place-header-badge" v-if="placeName" :style="badgeStyle">
                {{ placeName }}
            </div>
        </div>
    </header>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import BenefitsMenu from "./benefits-menu";
import { contrastColor, withHash } from "../helpers/color";

const MENU_MODALS = ["statement", "faq", "terms"];

export default {
    name: "PlaceHeader",
    components: { BenefitsMenu },
    props: {
        showBack: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapState({
            place: (state) => state.place,
            client: (state) => state.client,
            token: (state) => state.token,
        }),
        isLogged() {
            return !!(this.client && this.client.id && this.token);
        },
        benefitConfig() {
            return this.place.benefit_program || {};
        },
        placeName() {
            return this.benefitConfig.name || this.place.name;
        },
        logoUrl() {
            let url = this.benefitConfig.logo || this.place.logo;
            if (!url) return "";
            if (url.startsWith("http://") || url.startsWith("https://")) {
                return url;
            }
            return (window.config ? window.config.api_url : "https://app.kevi.io/") + (url.startsWith("/") ? url.slice(1) : url);
        },
        primaryColor() {
            return withHash(
                this.benefitConfig.primaryColor || this.place.mainColor || this.place.color || "#2aaa4c"
            );
        },
        badgeStyle() {
            return {
                backgroundColor: this.primaryColor,
                color: contrastColor(this.primaryColor),
            };
        },
    },
    methods: {
        ...mapMutations(["openModal", "closeModal"]),
        onMenuSelect(action) {
            if (action === "logout") {
                this.closeModal();
                this.$store.dispatch("logout");
                return;
            }

            if (MENU_MODALS.indexOf(action) !== -1) {
                this.openModal(action);
            }
        },
        goBack() {
            this.$emit("back");
        },
    },
};
</script>

<style lang="scss">
.place-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 1.75rem;
}

.place-header-bar {
  position: relative;
  width: 100%;
  min-height: clamp(6rem, 26vw, 8.5625rem);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: #f3f4f6;
  border-bottom: 1px solid #e5e7eb;
}

.place-header-logo {
  max-height: clamp(2.5rem, 14vw, 3.3125rem);
  max-width: 50%;
  object-fit: contain;
}

.place-header-back,
.place-header-menu {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 5;
}

.place-header-back {
  left: 0.75rem;

  @media (min-width: 48rem) {
    left: 4rem;
  }

  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 0.5rem;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
}

.place-header-menu {
  right: 0.75rem;

  @media (min-width: 48rem) {
    right: 4rem;
  }
}

.place-header-badge {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 50%);
  z-index: 1;
  padding: 0.5rem;
  border-radius: 0.5rem;
  font-size: clamp(1rem, 5vw, 1.25rem);
  font-weight: 400;
  line-height: 1.3;
  text-align: center;
  max-width: calc(100% - 7rem);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
